import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/features/utils/fetch-server";
import i18n from "@/features/utils/i18n";
import type { SessionMessage } from "../types";
import { usePublicSession } from "./use-public-session";
import { useSendPublicMessage } from "./use-send-public-message";
import { publicWebsocketBus } from "./use-websockets";

// How long the "…" bubble lingers after the last streamed chunk before we consider the
// agent idle again (tool call, retry, …). Short enough to feel reactive, long enough to
// not flicker between two chunks of the same sentence.
const STREAMING_IDLE_MS = 500;

// The same bubble while the model thinks: no chunk is coming, so the 500 ms countdown would
// hide it at once. The answer's first chunk (or the message) takes over; this is only the
// ceiling for a thought that never ends in either.
const THINKING_IDLE_MS = 30_000;

// A last bubble taller than this share of the viewport is read from its top (see the
// auto-scroll below), and is then left this many pixels of air above it.
const TALL_MESSAGE_RATIO = 0.75;
const TALL_MESSAGE_MARGIN_PX = 12;

/**
 * `sessionPublicId` is undefined until somebody speaks: the widget renders before a
 * conversation exists, and `ensureSession` is what brings one into being on the first
 * message (see use-session-handle.ts). Everything that reads the conversation is
 * therefore guarded on having one; everything that writes goes through `sendText`.
 */
export const useChat = (
  sessionPublicId: string | undefined,
  ensureSession?: () => Promise<string>,
) => {
  const [textInput, setTextInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const [optimisticMessages, setOptimisticMessages] = useState<
    SessionMessage[]
  >([]);
  const [authRequired, setAuthRequired] = useState(false);
  // We don't render the streamed text itself (a half-written answer mutating live reads
  // as robotic). We only use "chunks are arriving" as a liveness signal: the "…" bubble
  // shows while text flows and fades out `STREAMING_IDLE_MS` after it stops, so the
  // indicator naturally appears/disappears while the agent works.
  const [isStreaming, setIsStreaming] = useState(false);
  const streamingIdleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [removedTimestamps, setRemovedTimestamps] = useState<Set<number>>(
    new Set(),
  );
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch session data
  const { data: session, isLoading } = usePublicSession(sessionPublicId);
  const sendMessage = useSendPublicMessage();

  // Redirect to the most recent session in the transfer chain if this session has been transferred
  useEffect(() => {
    if (
      !isLoading &&
      session?.last_transfer_session_public_id &&
      session.last_transfer_session_public_id !== sessionPublicId
    ) {
      console.log(
        `[useChat] Session has been transferred to ${session.last_transfer_session_public_id}, redirecting...`,
      );
      navigate(`/c/${session.last_transfer_session_public_id}`, {
        replace: true,
      });
    }
  }, [
    session?.last_transfer_session_public_id,
    sessionPublicId,
    isLoading,
    navigate,
  ]);

  // Listen to websocket for real-time messages and chunks.
  //
  // Keyed on the id we already hold, not on the loaded session: on the first message the
  // conversation is created and spoken to in the same breath, and waiting for its first
  // load to join meant the reply could be published to a room nobody was in yet.
  useEffect(() => {
    if (!sessionPublicId) return;

    const room = `public/sessions/${sessionPublicId}/messages`;

    console.log(`[PublicChat] Joining websocket room: ${room}`);

    // Join the websocket room
    publicWebsocketBus.emit("join", null, { room });

    const clearIdleTimer = () => {
      if (streamingIdleTimer.current) {
        clearTimeout(streamingIdleTimer.current);
        streamingIdleTimer.current = null;
      }
    };

    // A chunk arrived: keep the "…" up and re-arm the idle countdown.
    const pingStreaming = (idleMs = STREAMING_IDLE_MS) => {
      clearIdleTimer();
      setIsStreaming(true);
      streamingIdleTimer.current = setTimeout(() => {
        streamingIdleTimer.current = null;
        setIsStreaming(false);
      }, idleMs);
    };

    const stopStreaming = () => {
      clearIdleTimer();
      setIsStreaming(false);
    };

    const handleWebsocketEvent = (
      data:
        | SessionMessage
        | { type: "chunk"; value: string }
        | { type: "thinking" }
        | { type: "action_required"; action: string; tool?: string }
        | { type: "transferred"; public_id: string }
        | { type: "identity_verified" }
        | { _remove?: number[]; _removed?: string[] },
    ) => {
      console.log("[PublicChat] Received websocket event:", data);

      // The agent handed the conversation to another agent: it continues in a brand new
      // session, so follow it there (a reload finds the same target through
      // `last_transfer_session_public_id`).
      if (
        "type" in data &&
        data.type === "transferred" &&
        data.public_id &&
        data.public_id !== sessionPublicId
      ) {
        navigate(`/c/${data.public_id}`, { replace: true });
        return;
      }

      // Signed in from the phone (the visitor confirmed it was them): the "appel en
      // cours" screen polls for it, this only makes it notice at once. Not a message.
      if ("type" in data && data.type === "identity_verified") {
        queryClient.invalidateQueries({
          queryKey: ["public-phone-handoff", sessionPublicId],
        });
        return;
      }

      // The backend asks the frontend to run an interactive step (e.g. the LLM
      // triggered a tool that requires a verified identity).
      if (
        "type" in data &&
        data.type === "action_required" &&
        data.action === "verify_identity"
      ) {
        setAuthRequired(true);
        return;
      }

      // Handle message removal — a turn the caller superseded mid-sentence is deleted
      // server-side, and its bubbles have to go with it. `_removed` carries row ids;
      // `_remove` is the older timestamp form, still accepted so a client ahead of the
      // backend keeps working.
      if (
        ("_remove" in data && Array.isArray(data._remove)) ||
        ("_removed" in data && Array.isArray(data._removed))
      ) {
        const timestamps = data._remove || [];
        const ids = data._removed || [];
        console.log("[PublicChat] Removing messages:", { timestamps, ids });
        if (timestamps.length)
          setRemovedTimestamps((prev) => new Set([...prev, ...timestamps]));
        if (ids.length) setRemovedIds((prev) => new Set([...prev, ...ids]));
        setOptimisticMessages((prev) =>
          prev.filter(
            (m) =>
              !timestamps.includes(m.timestamp) &&
              !(m.id && ids.includes(m.id)),
          ),
        );
        return;
      }

      // The model started to think: the "…" bubble comes up now, not at the first word.
      if ("type" in data && data.type === "thinking") {
        pingStreaming(THINKING_IDLE_MS);
        return;
      }

      // Handle streaming chunks — the text is discarded, only its arrival matters.
      if ("type" in data && data.type === "chunk") {
        pingStreaming();
        return;
      }

      // Handle complete messages
      const message = data as SessionMessage;
      console.log(
        "[PublicChat] Handling complete message, stopping the typing indicator",
      );

      // The final message replaces the indicator right away — no lingering "…" under a
      // message the agent already said.
      if (message.type === "agent") stopStreaming();

      // Add message optimistically
      setOptimisticMessages((prev) => {
        // Check if message already exists by timestamp
        const exists = prev.some((m) => m.timestamp === message.timestamp);
        if (exists) return prev;
        return [...prev, message];
      });
    };

    publicWebsocketBus.on(room, handleWebsocketEvent);

    return () => {
      console.log(`[PublicChat] Leaving websocket room: ${room}`);
      publicWebsocketBus.off(room);
      publicWebsocketBus.emit("leave", null, { room });
      stopStreaming();
      setOptimisticMessages([]);
      setRemovedTimestamps(new Set());
      setRemovedIds(new Set());
    };
  }, [sessionPublicId]);

  // Reconcile optimistic messages with actual session data
  useEffect(() => {
    if (!session?.messages) return;

    const serverTimestamps = new Set(session.messages.map((m) => m.timestamp));

    // Remove optimistic messages that are now in the server data
    setOptimisticMessages((prev) =>
      prev.filter((m) => !serverTimestamps.has(m.timestamp)),
    );
  }, [session?.messages]);

  // Combine all messages from previous sessions and current session
  const allMessagesWithSession: Array<
    SessionMessage & { sessionId: string; agentId: string }
  > = [];

  // Add messages from previous sessions (oldest first)
  if (session?.previous_sessions) {
    for (let i = session.previous_sessions.length - 1; i >= 0; i--) {
      const prevSession = session.previous_sessions[i];
      prevSession.messages.forEach((msg) => {
        allMessagesWithSession.push({
          ...msg,
          sessionId: prevSession.id,
          agentId: prevSession.agent_id,
        });
      });
    }
  }

  // Add messages from current session
  if (session?.messages) {
    session.messages.forEach((msg) => {
      allMessagesWithSession.push({
        ...msg,
        sessionId: session.id,
        agentId: session.agent_id,
      });
    });
  }

  // Add optimistic messages
  optimisticMessages.forEach((msg) => {
    allMessagesWithSession.push({
      ...msg,
      sessionId: session?.id || "",
      agentId: session?.agent_id || "",
    });
  });

  // Filter and sort all messages
  const allMessages = allMessagesWithSession
    .filter(
      (message) =>
        !removedTimestamps.has(message.timestamp) &&
        !(message.id && removedIds.has(message.id)),
    )
    .sort((a, b) => a.timestamp - b.timestamp)
    .filter((message) => {
      // Keep tool messages if they have a tool name
      if (message.type === "tool") {
        return message?.tool?.name?.trim();
      }
      // For regular messages, check if value has content
      return message.value?.trim();
    });

  // Auto-scroll when new messages arrive.
  //
  // Scrolling to the very bottom is right for a short turn, and wrong for a long one: a
  // ten-line answer lands with its *last* line against the input and the reader has to
  // scroll back up to find where it started. So when the last bubble is too tall to fit,
  // its first line is brought to the top of the viewport instead and the reader follows
  // it downward, the way they would read anything else.
  useEffect(() => {
    const timer = setTimeout(() => {
      const anchor = lastMessageRef.current;
      const viewport = anchor?.closest<HTMLElement>(
        "[data-radix-scroll-area-viewport]",
      );

      if (anchor && viewport) {
        const height = anchor.getBoundingClientRect().height;
        // "Doesn't comfortably fit" rather than "doesn't fit": a bubble filling most of
        // the viewport is already uncomfortable to read from its end.
        if (height > viewport.clientHeight * TALL_MESSAGE_RATIO) {
          const top =
            viewport.scrollTop +
            anchor.getBoundingClientRect().top -
            viewport.getBoundingClientRect().top -
            TALL_MESSAGE_MARGIN_PX;
          viewport.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
          return;
        }
      }

      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);

    return () => clearTimeout(timer);
  }, [allMessages.length, isStreaming, sendMessage.isPending]);

  /**
   * Say something as the visitor, without going through the input box. The form below
   * is one caller; the other is the host page opening the widget on a question of its
   * own (`HeyUpChat({ message })`).
   *
   * This is also where a conversation is *born*: the first message is what creates it,
   * which is why the id is resolved here rather than read from state. Before that there
   * is nothing on the server, and that is the point.
   */
  const sendText = async (text: string) => {
    if (!text.trim()) return false;

    try {
      const publicId =
        session?.public_id || sessionPublicId || (await ensureSession?.());
      if (!publicId) return false;

      await sendMessage.mutateAsync({ publicId, message: text });
      return true;
    } catch (error) {
      // Said in the visitor's terms: "Too Many Requests" in a corner of a chat widget
      // reads as nothing happened at all.
      toast.error(i18n.t("chat.send_failed", { ns: "public" }), {
        description: error instanceof Error ? error.message : undefined,
      });
      return false;
    }
  };

  /**
   * The host page's "prendre rendez-vous" (`HeyUpChat({ booking: true })`): the calendar
   * opens in the thread without a turn of the agent, under the visitor's own words when
   * the button carried some. A chat that cannot show one (no booking module) gets the
   * words as an ordinary message instead, and the agent answers them.
   */
  const openBookingCalendar = async (text: string) => {
    try {
      const publicId =
        session?.public_id || sessionPublicId || (await ensureSession?.());
      if (!publicId) return false;
      await api.post(
        `/api/sessions/v1/public/${publicId}/booking-calendar`,
        { message: text || undefined },
        { credentials: "include" },
      );
      void queryClient.invalidateQueries({
        queryKey: ["public-session", publicId],
      });
      return true;
    } catch {
      return text ? sendText(text) : false;
    }
  };

  const handleSendText = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!textInput.trim()) return;
    // Clear optimistically: the box must not hold a copy of what is already in flight.
    const text = textInput;
    setTextInput("");
    // Failed → hand the words back, unless the visitor has started writing the next
    // message in the meantime (the field stays live during the round-trip, so they can).
    if (!(await sendText(text)))
      setTextInput((current) => (current.trim() ? current : text));
  };

  return {
    session,
    isLoading,
    textInput,
    setTextInput,
    allMessages,
    messagesEndRef,
    lastMessageRef,
    handleSendText,
    sendText,
    openBookingCalendar,
    sendMessage,
    authRequired,
    setAuthRequired,
    // True while the agent is actively producing text (chunks arriving, plus a short
    // grace period). This is what the "…" indicator is keyed on.
    isStreaming,
  };
};
